/**
 * CameraGuide — Fullscreen camera with vertical guide overlay.
 * Uses YOLO coupon detector via POST /public/lotese/detect-coupon
 * to identify real coupons (not just pixel contrast).
 *
 * Lotese Story 2.2: Câmera Guiada com Compressão e Auto-Crop
 * Story 2.2-ext (2026-04-20): substituído contrast-stddev por ONNX YOLO11n no backend.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X, Camera } from "lucide-react";
import { toast } from "sonner";
import { cropToRect } from "./imageUtils";

interface CameraGuideProps {
  onCapture: (blob: Blob) => void;
  onClose: () => void;
  guideText?: string;
}

// Frequência do heartbeat de detecção. 1 Hz mantém UX fluida sem sobrecarregar
// o endpoint ONNX (cada inference ~150ms).
const DETECT_INTERVAL_MS = 1000;

// Dimensões do frame enviado ao detector — menor = mais rápido.
// 320×320 JPEG q70 ≈ 12-20KB, inferência ~100ms no servidor.
const PREVIEW_SIZE = 320;
const PREVIEW_QUALITY = 0.7;

// Guide rectangle: vertical (comprovante de loteria é formato portrait).
// Dimensões relativas à área visível da câmera.
const GUIDE_RECT_RATIO = { wRatio: 0.65, hRatio: 0.8 };

interface DetectResponse {
  detected: boolean;
  confidence: number;
  bbox: { x: number; y: number; w: number; h: number } | null;
}

/** Preview frame do video → Blob JPEG comprimido. */
async function frameToJpegBlob(
  video: HTMLVideoElement,
  maxSize: number,
  quality: number
): Promise<Blob | null> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (vw === 0 || vh === 0) return null;

  // Redimensiona mantendo aspect ratio — lado maior vira maxSize.
  const scale = Math.min(maxSize / vw, maxSize / vh);
  const w = Math.round(vw * scale);
  const h = Math.round(vh * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

export default function CameraGuide({
  onCapture,
  onClose,
  guideText = "Encaixe o comprovante dentro do retângulo",
}: CameraGuideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectInFlightRef = useRef<boolean>(false);
  const detectTimerRef = useRef<number | null>(null);

  const [detection, setDetection] = useState<DetectResponse>({
    detected: false,
    confidence: 0,
    bbox: null,
  });
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // ---- Start camera ----
  useEffect(() => {
    let cancelled = false;

    async function startCamera() {
      try {
        // Alta resolução é ESSENCIAL para OCR: sem width/height, o browser
        // escolhe default ~640×480 que deixa texto do comprovante ilegível.
        // 1920×1080 é o mínimo para OCR confiável; `ideal` deixa o browser
        // usar o mais próximo possível do sensor.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        } catch {
          if (!cancelled) {
            setCameraError(
              "Não foi possível acessar a câmera. Verifique as permissões do navegador."
            );
          }
        }
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (detectTimerRef.current) {
        window.clearTimeout(detectTimerRef.current);
      }
    };
  }, []);

  // ---- Polling do detector ----
  const runDetection = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || detectInFlightRef.current) {
      detectTimerRef.current = window.setTimeout(runDetection, DETECT_INTERVAL_MS);
      return;
    }

    detectInFlightRef.current = true;
    try {
      const blob = await frameToJpegBlob(video, PREVIEW_SIZE, PREVIEW_QUALITY);
      if (!blob) return;

      const form = new FormData();
      form.append("image", blob, "frame.jpg");

      const res = await fetch("/api/v1/public/lotese/detect-coupon", {
        method: "POST",
        body: form,
      });

      if (res.ok) {
        const data: DetectResponse = await res.json();
        setDetection(data);
      }
    } catch {
      // Falha de rede — mantém estado anterior, não quebra UX
    } finally {
      detectInFlightRef.current = false;
      detectTimerRef.current = window.setTimeout(runDetection, DETECT_INTERVAL_MS);
    }
  }, []);

  useEffect(() => {
    detectTimerRef.current = window.setTimeout(runDetection, DETECT_INTERVAL_MS);
    return () => {
      if (detectTimerRef.current) {
        window.clearTimeout(detectTimerRef.current);
      }
    };
  }, [runDetection]);

  // ---- Capture ----
  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || isCapturing) return;
    if (!detection.detected) {
      toast.warning("Nenhum comprovante detectado. Tente enquadrar melhor antes de capturar.");
    }
    setIsCapturing(true);

    try {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      // Se houver bbox do detector, usa ele (crop preciso). Senão, usa o guide.
      let rx: number, ry: number, rw: number, rh: number;
      if (detection.bbox) {
        rx = Math.round(detection.bbox.x * vw);
        ry = Math.round(detection.bbox.y * vh);
        rw = Math.round(detection.bbox.w * vw);
        rh = Math.round(detection.bbox.h * vh);
      } else {
        rw = Math.round(vw * GUIDE_RECT_RATIO.wRatio);
        rh = Math.round(vh * GUIDE_RECT_RATIO.hRatio);
        rx = Math.round((vw - rw) / 2);
        ry = Math.round((vh - rh) / 2);
      }

      const blob = await cropToRect(video, { x: rx, y: ry, w: rw, h: rh });

      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      onCapture(blob);
    } catch {
      setIsCapturing(false);
    }
  }, [isCapturing, detection, onCapture]);

  const handleClose = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onClose();
  }, [onClose]);

  if (cameraError) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black text-white p-6">
        <p className="text-lg text-center mb-6">{cameraError}</p>
        <Button variant="outline" className="h-14 text-lg" onClick={handleClose}>
          Fechar
        </Button>
      </div>
    );
  }

  const borderColor = detection.detected ? "border-green-500" : "border-red-500";
  const statusText = detection.detected
    ? `Comprovante detectado (${Math.round(detection.confidence * 100)}%) — toque para capturar`
    : guideText;

  // Renderiza via React Portal direto em document.body para escapar de
  // qualquer parent com `transform` ou `backdrop-filter` (que "prenderiam"
  // um `position: fixed` dentro do layout em vez do viewport).
  // No Portal do Parceiro, o PortalLayout e vários Cards usam
  // `backdrop-blur-sm`, o que quebrava o fullscreen da câmera.
  return createPortal(
    // `inset-0` ancora no LAYOUT viewport, que no Android é mais alto que a
    // área visível enquanto a barra de URL está na tela. Resultado: o `bottom`
    // dos controles ficava medido a partir de um fundo FORA DA TELA, e o botão
    // aparecia cortado — pior no Edge, que reporta menos que o Chrome.
    // `100dvh` (dynamic viewport height) acompanha a barra do navegador, então
    // o fundo do contêiner é o fundo VISÍVEL. Esta é a correção que faltava:
    // `env(safe-area-inset-*)` sozinho não resolve no Android, porque lá ele
    // devolve 0 (o inset existe para o entalhe do iPhone, não para a barra de
    // gestos do Android).
    <div
      className="fixed inset-x-0 top-0 z-[9999] flex flex-col items-center justify-center bg-black"
      style={{ height: "100dvh" }}
    >
      {/* Container quadrado (1:1). Reduz crop lateral de ~58% (3:4) para
          ~44% — cerca de 40% menos zoom em relação ao fix anterior. Em troca
          aparecem barras pretas de ~25% em cima e embaixo. UX: usuário vê
          mais da cena e enquadra o comprovante com menos esforço. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100vw",
          height: "100vw",
          maxHeight: "90dvh",
          objectFit: "cover",
          objectPosition: "center",
          backgroundColor: "black",
        }}
      />

      {/* Guide rectangle portrait, desenhado sobre o viewport (não sobre o
          video) — tamanho estável independente de como o video é exibido. */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ width: "100vw", height: "100dvh" }}
      >
        <div
          className={`border-[3px] ${borderColor} rounded-lg transition-colors duration-300`}
          style={{
            width: "min(60vmin, 75vw)",
            height: "min(80vmin, 65dvh)",
          }}
        />
      </div>

      {/* Status text */}
      {/* Mesmo tratamento do rodapé: `top-8` sozinho põe a instrução embaixo do
          entalhe/câmera frontal em iPhone. É a instrução que ensina a enquadrar
          o comprovante — escondê-la derrota o propósito da tela. */}
      <div
        className="absolute left-0 right-0 text-center pointer-events-none px-4"
        style={{ top: "max(3rem, calc(2rem + env(safe-area-inset-top, 0px)))" }}
        data-testid="camera-instrucao"
      >
        <span className="bg-black/60 text-white text-base px-4 py-2 rounded-full">
          {statusText}
        </span>
      </div>

      {/* Controles.

          Histórico: `bottom-8` sozinho encostava o botão na barra de gestos —
          o Augusto relatou "cortado, muito embaixo". A 1a tentativa somou
          `env(safe-area-inset-bottom)`, o que resolveu SÓ no iOS: no Android
          esse inset devolve 0, e o Augusto (Poco X7 Pro) continuou vendo o
          botão cortado, pior no Edge.

          Agora são duas defesas independentes, porque uma só não cobre os dois
          sistemas:

            1. o contêiner usa `100dvh` (acima) — o fundo passa a ser o VISÍVEL,
               não o do layout viewport;
            2. `max(...)` garante um PISO de 4rem de folga mesmo onde o `env()`
               devolve 0 — a barra de gestos do Android tem ~48px, e 4rem=64px
               cobre com margem.

          O `max()` é o que torna a proteção independente de plataforma: quem
          tem inset (iPhone) ganha o inset + 2rem; quem não tem (Android,
          desktop) ganha o piso. */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center gap-8"
        style={{
          bottom: "max(4rem, calc(2rem + env(safe-area-inset-bottom, 0px)))",
        }}
        data-testid="camera-controles"
      >
        <button
          onClick={handleClose}
          className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center text-white"
          aria-label="Fechar câmera"
        >
          <X className="h-7 w-7" />
        </button>

        {/* 80px em vez de 64px: o Augusto pediu "uma área um pouco maior".
            É o alvo principal da tela e é acionado com o polegar, muitas vezes
            com uma mão só segurando o comprovante. O espaçador ao lado tem o
            mesmo tamanho do botão de fechar, para manter a captura CENTRADA na
            tela — sem ele o trio fica deslocado para a direita. */}
        <button
          onClick={handleCapture}
          disabled={isCapturing}
          className="h-20 w-20 rounded-full bg-white border-4 border-white/50 flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"
          aria-label="Capturar foto"
        >
          <Camera className="h-9 w-9 text-black" />
        </button>

        <div className="h-14 w-14" />
      </div>
    </div>,
    document.body
  );
}
